import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

const AppContext = createContext({ isApp: false });

/**
 * Provides access to the outter App context (if any) - isApp field indicates if app operations
 * are available.
 */
export const useApp = () => useContext(AppContext);

/**
 * This provider handles messaging between the App and an outter ReactNativeWebView where the
 * website is running inside one of our apps and needs to leverage app capbilities not normally
 * available throught he browser
 * @component
 */
export const AppProvider = ({ children }) => {
    const isApp = useMemo(() => !!window.ReactNativeWebView, []);
    const listenerRegistry = useRef(new Map());

    const on = useCallback((type, callback) => {
        if (!listenerRegistry.current.has(type)) {
            listenerRegistry.current.set(type, [callback]);
        } else if (!listenerRegistry.current.get(type).includes(callback)) {
            listenerRegistry.current.get(type).push(callback);
        }
        return () => {
            if (listenerRegistry.current.has(type)) {
                const callbacks = listenerRegistry.current.get(type);
                const idx = callbacks.findIndex(c => c === callback);
                callbacks.splice(idx, 1);
                if (callbacks.length === 0) {
                    listenerRegistry.current.delete(type);
                }
            }
        }
    }, []);

    const emit = useCallback((type, data) => {
        if (window.ReactNativeWebView) {
            if (window.APP_VERSION > 1) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type, data }));
            } else {
                window.ReactNativeWebView.postMessage(JSON.stringify({ event: type, data }));
            }
        }
    }, []);

    useEffect(() => {
        const listener = msg => {
            const data = msg.data;
            try {
                if (window.APP_VERSION > 1) {
                    if (data.type && listenerRegistry.current?.has(data.type)) {
                        listenerRegistry.current.get(data.type).forEach(callback => callback(data.data));
                    }
                } else {
                    if (data.event && listenerRegistry.current?.has(data.event)) {
                        listenerRegistry.current.get(data.event).forEach(callback => callback(data.data));
                    }
                }
            } catch {

            }
        }
        window.addEventListener('message', listener);
        return () => window.removeEventListener('message', listener);
    }, []);

    const value = useMemo(() => ({ isApp, on, emit }), [isApp, on, emit]);

    return <AppContext.Provider value={value}>
        {children}
    </AppContext.Provider>
}